import { chatService } from '@/lib/services/chat.service';
import {
  Conversation,
  ConversationListItem,
} from '@/types/chat/conversations.types';
import { observable } from '@legendapp/state';
import { Session } from 'next-auth';

export const conversationsState = observable<{
  conversations: ConversationListItem[];
  activeConversation: Conversation | null;
  isLoading: boolean;
  error: Error | null;
}>({
  conversations: [],
  activeConversation: null,
  isLoading: false,
  error: null,
});

// Store polling interval outside of observable
let pollingInterval: NodeJS.Timeout | null = null;

export const conversationsActions = {
  fetchConversations: async (session: Session) => {
    conversationsState.isLoading.set(true);
    try {
      const conversations = await chatService.getConversations(session);
      conversationsState.conversations.set(conversations);
    } catch (error) {
      conversationsState.error.set(error as Error);
    } finally {
      conversationsState.isLoading.set(false);
    }
  },
  fetchConversationById: async (id: string, session: Session) => {
    conversationsState.isLoading.set(true);
    try {
      const conversation = await chatService.getConversation(id, session);
      if (conversation) {
        conversationsState.activeConversation.set(conversation);

        // Also update the conversation in the list if it exists
        const currentConversations = conversationsState.conversations.get();
        const updatedConversations = currentConversations.map((conv) =>
          conv.id === conversation.id
            ? {
                ...conv,
                participants: conversation.participants,
                lastMessage:
                  conversation.messages[conversation.messages.length - 1],
                updatedAt: conversation.updatedAt,
              }
            : conv
        );
        conversationsState.conversations.set(updatedConversations);
      }
    } catch (error) {
      conversationsState.error.set(error as Error);
    } finally {
      conversationsState.isLoading.set(false);
    }
  },
  setActiveConversation: (conversation: Conversation) => {
    conversationsState.activeConversation.set(conversation);
  },
  setActiveConversationById: async (id: string, session?: Session) => {
    // First try to find it in cached conversations
    const cachedConversation = conversationsState.conversations
      .get()
      .find((c) => c.id === id);

    if (cachedConversation) {
      // Convert ConversationListItem to Conversation for active state
      const fullConversation: Conversation = {
        ...cachedConversation,
        messages: cachedConversation.lastMessage
          ? [cachedConversation.lastMessage]
          : [],
      };
      conversationsState.activeConversation.set(fullConversation);
    }

    // Always fetch the full conversation data to ensure we have all messages
    if (session) {
      await conversationsActions.fetchConversationById(id, session);
    }
  },
  createConversation: async (receiverId: string, session: Session) => {
    conversationsState.isLoading.set(true);
    try {
      const conversation = await chatService.createConversation(
        receiverId,
        session
      );

      // Fetch the full conversation with participants and messages
      await conversationsActions.fetchConversationById(
        conversation.id,
        session
      );

      return conversation;
    } catch (error) {
      conversationsState.error.set(error as Error);
    } finally {
      conversationsState.isLoading.set(false);
    }
  },
  createMessage: async (
    conversationId: string,
    content: string,
    session: Session
  ) => {
    try {
      const message = await chatService.createMessage(
        conversationId,
        content,
        session
      );

      // Ensure message has sender information
      let messageWithSender = message;
      if (!message.sender && message.senderId) {
        // Try to find sender in active conversation participants
        const activeConversation = conversationsState.activeConversation.get();
        if (activeConversation) {
          const sender = activeConversation.participants.find(
            (p) => p.id === message.senderId
          );
          if (sender) {
            messageWithSender = { ...message, sender };
          } else if (message.senderId === session.user.id) {
            // Fallback to session user info
            messageWithSender = {
              ...message,
              sender: {
                id: session.user.id,
                name: session.user.name || 'You',
                email: session.user.email || '',
                avatar: session.user.image || undefined,
                role: 'user',
                bio: undefined,
              },
            };
          }
        }
      }

      // Update active conversation with new message (immutable)
      const activeConversation = conversationsState.activeConversation.get();
      if (activeConversation && activeConversation.id === conversationId) {
        const updatedConversation = {
          ...activeConversation,
          messages: [...activeConversation.messages, messageWithSender],
          updatedAt: new Date().toISOString(),
        };
        conversationsState.activeConversation.set(updatedConversation);
      }

      // Update conversations list to reflect latest message
      const conversations = conversationsState.conversations.get();
      const conversationIndex = conversations.findIndex(
        (c) => c.id === conversationId
      );
      if (conversationIndex !== -1) {
        const updatedConversations = [...conversations];
        updatedConversations[conversationIndex] = {
          ...updatedConversations[conversationIndex],
          lastMessage: messageWithSender,
          updatedAt: new Date().toISOString(),
        };
        conversationsState.conversations.set(updatedConversations);
      }

      return messageWithSender;
    } catch (error) {
      conversationsState.error.set(error as Error);
      throw error;
    }
  },

  // Polling mechanism for real-time updates
  startPolling: (session: Session, intervalMs: number = 3000) => {
    // Clear any existing polling
    conversationsActions.stopPolling();

    const pollForUpdates = async () => {
      try {
        const activeConversation = conversationsState.activeConversation.get();
        if (activeConversation) {
          // Fetch the latest conversation data
          const updatedConversation = await chatService.getConversation(
            activeConversation.id,
            session
          );

          if (updatedConversation) {
            // Check if there are new messages or if updatedAt changed
            const hasNewMessages =
              updatedConversation.messages.length >
              activeConversation.messages.length;
            const hasUpdatedTime =
              new Date(updatedConversation.updatedAt) >
              new Date(activeConversation.updatedAt);

            if (hasNewMessages || hasUpdatedTime) {
              console.log('Polling: Updating conversation with new data', {
                oldLength: activeConversation.messages.length,
                newLength: updatedConversation.messages.length,
                oldTime: activeConversation.updatedAt,
                newTime: updatedConversation.updatedAt,
              });

              conversationsState.activeConversation.set(updatedConversation);

              // Also update the conversations list
              const conversations = conversationsState.conversations.get();
              const conversationIndex = conversations.findIndex(
                (c) => c.id === activeConversation.id
              );
              if (conversationIndex !== -1) {
                const updatedConversations = [...conversations];
                updatedConversations[conversationIndex] = {
                  ...updatedConversations[conversationIndex],
                  participants: updatedConversation.participants,
                  lastMessage:
                    updatedConversation.messages[
                      updatedConversation.messages.length - 1
                    ],
                  updatedAt: updatedConversation.updatedAt,
                };
                conversationsState.conversations.set(updatedConversations);
              }
            }
          }
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    };

    // Store the interval ID in external variable
    pollingInterval = setInterval(pollForUpdates, intervalMs);
  },

  stopPolling: () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      pollingInterval = null;
    }
  },
};
